<template>
  <div
    v-if="phase"
    class="phase-pill"
    :style="{
      backgroundColor: displayInfo.bgColor,
      borderColor: displayInfo.borderColor,
      color: displayInfo.color,
    }"
    :title="tooltipText"
  >
    <span class="phase-emoji">{{ displayInfo.emoji }}</span>
    <span class="phase-label">{{ displayInfo.label }}</span>
    <span v-if="weekLabel" class="phase-week">· {{ weekLabel }}</span>
    <span v-if="isOverride" class="phase-override">Override</span>
  </div>
</template>

<script>
import { SeasonPhases } from "@/services/season-phases-service";

export default {
  name: "PhasePill",

  props: {
    clubId: {
      type: [Number, String],
      default: null,
    },
    teamId: {
      type: [Number, String],
      default: null,
    },
    showWeek: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      phase: null,
      source: null,
      phaseConfig: null,
    };
  },

  computed: {
    displayInfo() {
      return SeasonPhases.getPhaseDisplayInfo(this.phase || "offseason");
    },

    isOverride() {
      return this.source === "team_override" || this.source === "club_override";
    },

    /**
     * Week N within the current phase. Returns null when we can't compute
     * (e.g. override active, dates missing, calculation out of bounds).
     */
    weekLabel() {
      if (!this.showWeek) return null;
      if (this.isOverride) return null;
      if (!this.phaseConfig || !this.phase) return null;

      const startKey = this.phaseStartKey(this.phase);
      const start = this.phaseConfig[startKey];
      if (!start) return null;

      const startDate = new Date(start);
      const today = new Date();
      const msPerWeek = 7 * 24 * 60 * 60 * 1000;
      const diff = today - startDate;
      if (diff < 0) return null;

      const week = Math.floor(diff / msPerWeek) + 1;
      if (week < 1 || week > 60) return null;

      return `Week ${week}`;
    },

    tooltipText() {
      const sourceText = {
        calculated: "Calculated from the club calendar",
        club_override: "Admin override active",
        team_override: "Team override active",
      };
      return sourceText[this.source] || "Current season phase";
    },
  },

  watch: {
    clubId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
    teamId() {
      this.load();
    },
  },

  methods: {
    phaseStartKey(phase) {
      if (phase === "regular") return "regular_season_start";
      if (phase === "preseason") return "preseason_start";
      if (phase === "postseason") return "postseason_start";
      if (phase === "offseason") return "offseason_start";
      return null;
    },

    async load() {
      if (!this.clubId) return;
      try {
        const [phaseRes, configRes] = await Promise.all([
          SeasonPhases.getCurrentPhase(this.clubId, this.teamId),
          SeasonPhases.getPhaseConfig(this.clubId),
        ]);
        if (phaseRes.data && phaseRes.data.success) {
          this.phase = phaseRes.data.data.phase;
          this.source = phaseRes.data.data.source;
        }
        if (configRes.data && configRes.data.success) {
          this.phaseConfig = configRes.data.data;
        }
      } catch (error) {
        console.error("PhasePill: failed to load phase data", error);
      }
    },
  },
};
</script>

<style scoped>
.phase-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 1px solid transparent;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.4;
}

.phase-emoji {
  font-size: 1em;
}

.phase-label {
  font-weight: 600;
}

.phase-week {
  opacity: 0.85;
}

.phase-override {
  margin-left: 4px;
  padding: 1px 6px;
  background: rgba(220, 53, 69, 0.85);
  color: white;
  border-radius: 8px;
  font-size: 0.65rem;
  font-weight: 600;
}
</style>
